import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress
} from '@mui/material';
import NotificationSystem from '../components/NotificationSystem';
import SignatureHTMLRenderer from '../components/SignatureHTMLRenderer';
import { useNotification } from '../hooks/useNotification';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

interface Template {
  id: number;
  name: string;
  html_content: string;
  domain: string;
  created_at: string;
  updated_at: string;
}

const Templates: React.FC = () => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Estados para o modal de edição
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
  const [isNewTemplate, setIsNewTemplate] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    html_content: '',
    domain: ''
  });
  const [editLoading, setEditLoading] = useState(false);
  
  // Sistema de notificações padronizado
  const { notification, showSuccess, showError } = useNotification();

  const [userData, setUserData] = useState<any>(null);
  const [userAliases, setUserAliases] = useState<any[]>([]);

  const availableDomains = ['acspro.com.br', 'accyberpro.com.br', 'podcafe.tech', 'acsflow.com.br'];
  const availableVariables = [
    { key: 'name', label: 'Nome' },
    { key: 'title', label: 'Cargo' },
    { key: 'photo_id', label: 'ID da Foto' },
    { key: 'email', label: 'Email' },
    { key: 'mobile_block', label: 'Celular' }
  ];

  // Carregar templates do banco de dados
  const loadTemplates = async () => {
    setLoading(true);
    setError(null);
    try {
          const token = localStorage.getItem('authToken');
    const response = await fetch('/api/templates', {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
      
      if (!response.ok) {
        throw new Error('Erro ao carregar templates');
      }
      
      const data = await response.json();
      setTemplates(data);
    } catch (error) {
      console.error('Erro ao carregar templates:', error);
      setError('Erro ao carregar templates do banco de dados');
      showError('Erro ao carregar templates');
    } finally {
      setLoading(false);
    }
  };

  // Carregar dados do usuário logado
  const loadUserData = async () => {
    try {
      // Buscar dados do usuário diretamente da tabela users (igual ao backend)
          const token = localStorage.getItem('authToken');
    const response = await fetch('/api/users/me', {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
      
      if (response.ok) {
        const user = await response.json();

        setUserData({
          name: user.name,
          title: user.title,
          mobile: user.mobile,
          email: user.email,
          photo_id: user.photo_id,
          show_title: user.show_title !== undefined ? user.show_title : true
        });
      }
    } catch (error) {
      console.error('Erro ao carregar dados do usuário:', error);
    }
  };

  // Carregar aliases do usuário
  const loadUserAliases = async () => {
    try {

          const token = localStorage.getItem('authToken');
    const response = await fetch('/api/users/me/aliases', {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
      

      
      if (response.ok) {
        const data = await response.json();

        setUserAliases(data);
      } else {
        console.error('Erro na resposta aliases:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Erro ao carregar aliases do usuário:', error);
    }
  };



  useEffect(() => {
    loadTemplates();
    loadUserData();
    loadUserAliases();
  }, []);

  // Forçar atualização do preview quando o domínio ou dados do usuário mudarem
  useEffect(() => {
    // O preview será atualizado automaticamente quando editForm.domain, userAliases ou userData mudarem
    // porque getPreviewContent() é chamada a cada render
  }, [editForm.domain, userAliases, userData]);

  const handleEditTemplate = (templateId: number) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setEditForm({
        name: template.name || '',
        html_content: template.html_content || '',
        domain: template.domain || ''
      });
      setEditingTemplate(template);
      setIsNewTemplate(false);
      setEditDialogOpen(true);
    }
  };

  const handleNewTemplate = () => {
    setEditForm({
      name: '',
      html_content: '',
      domain: ''
    });
    setEditingTemplate(null);
    setIsNewTemplate(true);
    setEditDialogOpen(true);
  };

  const handleEditFormChange = (field: string, value: string | boolean) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const insertVariable = (variable: string) => {
    const textarea = document.getElementById('template-content') as HTMLTextAreaElement;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = textarea.value;
      const before = text.substring(0, start);
      const after = text.substring(end);
      
      const newText = before + `{{${variable}}}` + after;
      setEditForm(prev => ({ ...prev, html_content: newText }));
      
      // Focar no textarea e posicionar cursor após a variável inserida
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + variable.length + 4, start + variable.length + 4);
      }, 0);
    }
  };

  // Função para formatar celular brasileiro (igual ao backend)
  const formatarCelularBR = (mobile: string | undefined): string => {
    if (!mobile) return '';
    // Remove todos os caracteres não numéricos primeiro
    let num = mobile.replace(/\D/g, '');
    
    // Se não tem números, retorna vazio
    if (!num || num.length === 0) return '';
    
    // Remove código do país se presente (55)
    if (num.length === 13 && num.startsWith('55')) num = num.slice(2);
    if (num.length === 12 && num.startsWith('55')) num = num.slice(2);
    
    // Remove zero inicial se presente
    if (num.length === 11 && num.startsWith('0')) num = num.slice(1);
    
    // Formata números de 11 dígitos (celular): (XX) XXXXX-XXXX
    if (num.length === 11) {
      return num.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    }
    
    // Formata números de 10 dígitos (fixo): (XX) XXXX-XXXX
    if (num.length === 10) {
      return num.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    }
    
    // Se não tem o tamanho esperado (9, 10 ou 11 dígitos), retorna o número limpo
    // Mas tenta formatar se tiver pelo menos 9 dígitos
    if (num.length >= 9) {
      // Tenta formatar como celular se tiver 9 ou mais dígitos
      if (num.length === 9) {
        return num.replace(/(\d{2})(\d{4})(\d{3})/, '($1) $2-$3');
      }
    }
    
    return num;
  };

  const getPreviewContent = () => {
    let content = editForm.html_content;
    
    // Encontrar o alias correto baseado no domínio do template
    const matchingAlias = userAliases.find(alias => alias.domain === editForm.domain);
    
    // Usar dados reais do usuário logado para o preview (igual ao backend)
    const rawMobile = userData?.mobile || '(11) 2626-4653';
    // Garantir que o mobile seja formatado corretamente (remove espaços, hífens, etc e formata com parênteses)
    // A função formatarCelularBR sempre formata números de 11 dígitos como (XX) XXXXX-XXXX
    // IMPORTANTE: Sempre formatar, mesmo que o número já tenha espaços ou hífens
    const formattedMobile = formatarCelularBR(rawMobile);
    
    // Verificar se deve mostrar o título baseado no campo show_title
    const showTitle = userData?.show_title !== undefined ? userData.show_title : true;
    const titleValue = showTitle ? (userData?.title || 'Desenvolvedor Full Stack') : '';
    
    const userVars = {
      name: userData?.name || 'Tainã Rodrigues',
      title: titleValue, // Só inclui título se show_title for true
      mobile: formattedMobile, // Mobile já formatado com parênteses
      email: matchingAlias?.email || userData?.email || 'admin@acspro.com.br',
      photo_id: matchingAlias?.photo_id || userData?.photo_id || 'https://lh3.googleusercontent.com/a/ACg8ocJN_1234567890abcdef'
    };
    
    // Processar {{MOBILE_BLOCK}} primeiro (igual ao backend)
    // IMPORTANTE: usar o mobile formatado (já com parênteses)
    let mobileBlock = '';
    if (userVars.mobile && userVars.mobile.trim() !== '') {
      // O mobile já está formatado como "(62) 98139-1121" pela função formatarCelularBR
      // Usamos o número formatado para exibição e removemos não numéricos apenas para o link tel:
      const mobileNumbersOnly = userVars.mobile.replace(/\D/g, ''); // Remove tudo exceto números para o link
      mobileBlock = ` | <a href="tel:+55${mobileNumbersOnly}" target="_blank">${userVars.mobile}</a>`;
    }
    
    // Substituir {{MOBILE_BLOCK}} primeiro (igual ao backend)
    // Suporta variações: {{MOBILE_BLOCK}}, {{mobile_block}}, {{|MOBILE_BLOCK}}
    content = content.replace(new RegExp(`\{\{\\|?MOBILE_BLOCK\}\}`, 'gi'), mobileBlock);
    content = content.replace(new RegExp(`\{\{\\|?mobile_block\}\}`, 'gi'), mobileBlock);

    // Substituir photo_id pelo ID da foto (igual ao backend)
    if (userVars.photo_id) {
      content = content.replace(new RegExp(`{{photo_id}}`, 'gi'), userVars.photo_id);
    }

    // Substituir logo_url por uma URL padrão (igual ao backend)
    content = content.replace(new RegExp(`{{logo_url}}`, 'gi'), 'https://via.placeholder.com/200x100?text=Logo');

    // Depois substituir as outras variáveis (igual ao backend)
    Object.entries(userVars).forEach(([key, value]) => {
      if (key !== 'photo_id') { // Pular photo_id pois já foi processado
        const regex = new RegExp(`{{${key}}}`, 'gi');
        content = content.replace(regex, value || ''); // Substitui por string vazia se value for vazio
      }
    });

    return content;
  };

  const handleSaveEdit = async () => {
    setEditLoading(true);
    try {
      let response;
      
      if (isNewTemplate) {
        // Criar novo template
        const token = localStorage.getItem('authToken');
        response = await fetch('/api/templates', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            name: editForm.name,
            html_content: editForm.html_content,
            domain: editForm.domain
          })
        });

        if (!response.ok) {
          throw new Error('Erro ao criar template');
        }

        const newTemplate = await response.json();
        setTemplates([...templates, newTemplate]);

        showSuccess('Template criado com sucesso!');
      } else {
        // Atualizar template existente
        if (!editingTemplate) return;

        const token = localStorage.getItem('authToken');
        response = await fetch(`/api/templates/${editingTemplate.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            name: editForm.name,
            html_content: editForm.html_content,
            domain: editForm.domain
          })
        });

        if (!response.ok) {
          throw new Error('Erro ao atualizar template');
        }

        const updatedTemplate = await response.json();

        // Atualizar a lista local
        setTemplates(templates.map(t => 
          t.id === editingTemplate.id ? updatedTemplate : t
        ));

        showSuccess('Template atualizado com sucesso!');
      }

      setEditDialogOpen(false);
      setEditingTemplate(null);
      setIsNewTemplate(false);
    } catch (error) {
      console.error('Erro ao salvar template:', error);
      showError(isNewTemplate ? 'Erro ao criar template' : 'Erro ao atualizar template');
    } finally {
      setEditLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditDialogOpen(false);
    setEditingTemplate(null);
    setIsNewTemplate(false);
    setEditForm({
      name: '',
      html_content: '',
      domain: ''
    });
  };

  const handleDuplicateTemplate = async (templateId: number) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch('/api/templates', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
                     body: JSON.stringify({
             name: `${template.name} (Cópia)`,
             html_content: template.html_content,
             domain: template.domain
           })
        });

        if (!response.ok) {
          throw new Error('Erro ao duplicar template');
        }

        const newTemplate = await response.json();
        setTemplates([...templates, newTemplate]);
        
        showSuccess('Template duplicado com sucesso!');
      } catch (error) {
        console.error('Erro ao duplicar template:', error);
        showError('Erro ao duplicar template');
      }
    }
  };

  const handleDeleteTemplate = async (templateId: number) => {
    if (window.confirm('Tem certeza que deseja excluir este template?')) {
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(`/api/templates/${templateId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error('Erro ao excluir template');
        }

        setTemplates(templates.filter(t => t.id !== templateId));
        showSuccess('Template excluído com sucesso!');
      } catch (error) {
        console.error('Erro ao excluir template:', error);
        showError('Erro ao excluir template');
      }
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        width: '100%',
        minHeight: '100vh', 
        bgcolor: 'background.default', 
        py: 2
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ 
        width: '100%',
        minHeight: '100vh', 
        bgcolor: 'background.default', 
        py: 2
      }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={loadTemplates}>
          Tentar Novamente
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      width: '100%',
      minHeight: '100vh', 
      bgcolor: 'background.default', 
      py: 2
    }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
          Templates de Assinatura
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Gerencie os templates de assinatura de email
        </Typography>
      </Box>

      <Box sx={{ mb: 3 }}>
                 <Button
           variant="contained"
           startIcon={<AddIcon />}
           sx={{ mb: 2 }}
           onClick={handleNewTemplate}
         >
           Novo Template
         </Button>
      </Box>

      {templates.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Nenhum template encontrado
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Crie seu primeiro template de assinatura
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {templates.map((template) => (
            <Grid item xs={12} sm={6} md={4} key={template.id}>
              <Card 
                sx={{ 
                  height: 320, // Altura fixa para cards quadrados
                  display: 'flex', 
                  flexDirection: 'column',
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.2s ease',
                  border: '1px solid #e0e0e0',
                  '&:hover': {
                    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)'
                  }
                }}
              >
                {/* Header compacto */}
                <Box sx={{ 
                  p: 2, 
                  background: '#f8f9fa',
                  borderBottom: '1px solid #e0e0e0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  minHeight: 60
                }}>
                  <Typography variant="h6" component="h2" sx={{ 
                    fontWeight: 600,
                    fontSize: '1rem',
                    color: '#2c3e50'
                  }}>
                    {template.name}
                  </Typography>
                  <Chip
                    label="Template"
                    color="primary"
                    size="small"
                    sx={{
                      fontWeight: 500,
                      fontSize: '0.7rem',
                      height: 24,
                      color: 'white',
                      '& .MuiChip-label': {
                        color: 'white !important'
                      },
                      '& .MuiChip-icon': {
                        color: 'white !important'
                      }
                    }}
                  />
                </Box>

                {/* Conteúdo do Card */}
                <CardContent sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
                  {/* Informações do Domínio */}
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>
                      Domínio: {template.domain || 'Não definido'}
                    </Typography>
                  </Box>

                  {/* Status do Conteúdo */}
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>
                      Conteúdo HTML disponível
                    </Typography>
                  </Box>

                  {/* Informações de Data */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>
                      Criado: {template.created_at ? new Date(template.created_at).toLocaleDateString('pt-BR') : 'Data não disponível'}
                    </Typography>
                  </Box>

                  {/* Botões de Ação */}
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-between', mt: 'auto' }}>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<EditIcon />}
                      onClick={() => handleEditTemplate(template.id)}
                      sx={{ 
                        backgroundColor: '#1976d2',
                        color: 'white',
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: '0.7rem',
                        py: 0.5,
                        px: 1.5,
                        flex: 1,
                        '&:hover': {
                          backgroundColor: '#1565c0',
                        }
                      }}
                    >
                      Editar
                    </Button>
                    
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<CopyIcon />}
                      onClick={() => handleDuplicateTemplate(template.id)}
                      sx={{ 
                        backgroundColor: '#2e7d32',
                        color: 'white',
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: '0.7rem',
                        py: 0.5,
                        px: 1.5,
                        flex: 1,
                        '&:hover': {
                          backgroundColor: '#1b5e20',
                        }
                      }}
                    >
                      Duplicar
                    </Button>
                  </Box>
                </CardContent>

                {/* Footer com Ação Destrutiva */}
                <Box sx={{ 
                  p: 2, 
                  pt: 1,
                  borderTop: '1px solid #f0f0f0',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: 50
                }}>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<DeleteIcon />}
                    onClick={() => handleDeleteTemplate(template.id)}
                    sx={{ 
                      backgroundColor: '#d32f2f',
                      color: 'white',
                      textTransform: 'none',
                      fontWeight: 500,
                      fontSize: '0.7rem',
                      py: 0.5,
                      px: 1.5,
                      '&:hover': {
                        backgroundColor: '#c62828',
                      }
                    }}
                  >
                    Excluir
                  </Button>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Modal de Edição */}
             <Dialog 
         open={editDialogOpen} 
         onClose={handleCancelEdit}
         maxWidth="xl"
         fullWidth
         sx={{ '& .MuiDialog-paper': { height: '95vh', width: '95vw' } }}
       >
                 <DialogTitle>
           {isNewTemplate ? 'Novo Template' : 'Editar Template'}
           {editingTemplate && !isNewTemplate && (
             <Typography variant="body2" color="text.secondary">
               {editingTemplate.name}
             </Typography>
           )}
         </DialogTitle>
                 <DialogContent sx={{ height: 'calc(95vh - 140px)', overflow: 'hidden' }}>
           <Box sx={{ pt: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Nome do Template"
                  value={editForm.name}
                  onChange={(e) => handleEditFormChange('name', e.target.value)}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Domínio Associado</InputLabel>
                  <Select
                    value={editForm.domain}
                    label="Domínio Associado"
                    onChange={(e) => handleEditFormChange('domain', e.target.value)}
                  >
                    {availableDomains.map((domain) => (
                      <MenuItem key={domain} value={domain}>
                        {domain}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

                         <Box sx={{ flex: 1, display: 'flex', gap: 3, height: 'calc(100% - 80px)' }}>
               {/* Coluna do Editor */}
               <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                 <Box sx={{ mb: 2 }}>
                   <Typography variant="subtitle2" gutterBottom>
                     Variáveis Disponíveis (clique para inserir):
                   </Typography>
                   <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                     {availableVariables.map((variable) => (
                       <Chip
                         key={variable.key}
                         label={variable.label}
                         size="small"
                         clickable
                         onClick={() => insertVariable(variable.key)}
                         sx={{ cursor: 'pointer' }}
                       />
                     ))}
                   </Box>
                 </Box>
                 
                 <TextField
                   id="template-content"
                   label="Conteúdo HTML do Template"
                   value={editForm.html_content}
                   onChange={(e) => handleEditFormChange('html_content', e.target.value)}
                   fullWidth
                   multiline
                   rows={30}
                   sx={{ flex: 1, minHeight: '500px' }}
                 />
               </Box>

               {/* Coluna da Prévia */}
               <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                 <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                   <Typography variant="subtitle2">
                     Prévia em Tempo Real:
                   </Typography>
                   <Button
                     size="small"
                     startIcon={<RefreshIcon />}
                     onClick={() => {
                       loadUserData();
                       loadUserAliases();
                     }}
                     sx={{ minWidth: 'auto' }}
                   >
                     Atualizar
                   </Button>
                 </Box>
                 <Box 
                   sx={{ 
                     flex: 1, 
                     border: '1px solid #ddd', 
                     borderRadius: 1, 
                     p: 2, 
                     overflow: 'auto',
                     backgroundColor: '#fafafa',
                     minHeight: '500px'
                   }}
                 >
                   <SignatureHTMLRenderer 
                     html={getPreviewContent()}
                     style={{ 
                       minHeight: '450px',
                       fontFamily: 'Arial, sans-serif',
                       fontSize: '14px',
                       lineHeight: '1.5'
                     }}
                   />
                 </Box>
               </Box>
             </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEdit} disabled={editLoading}>
            Cancelar
          </Button>
                     <Button 
             onClick={handleSaveEdit} 
             variant="contained"
             disabled={editLoading || !editForm.name || !editForm.html_content || !editForm.domain}
           >
             {editLoading ? 'Salvando...' : (isNewTemplate ? 'Criar' : 'Salvar')}
           </Button>
        </DialogActions>
      </Dialog>

      {/* Sistema de notificações padronizado */}
      <NotificationSystem
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={notification.onClose || (() => {})}
      />
    </Box>
  );
};

export default Templates;
